import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../utils/supabaseClient";
import ShieldFooterIMG from "../../assets/shield.png";
import AddToCalendar from "../events/AddToCalendar";
import "./rockin-road-home.css";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const details = [
  { label: "Date", value: "Sept. 26, 2026" },
  { label: "Doors", value: "4 PM" },
  { label: "Dinner & Music", value: "5 PM" },
  { label: "Venue", value: "115 Bourbon St.", href: "https://www.115bourbonstreet.com/" },
  { label: "Dinner", value: "Free buffet dinner" },
];

const externalLinks = {
  roadHome: "https://www.roadhomeprogram.org",
  venue: "https://www.115bourbonstreet.com/",
  band: "https://therightstuffentertainment.com/artists/theronburgundys.html",
};

const featureImages = {
  bandPromo: "/media/rockin/the-ron-burgundys-promo.jpeg",
  bandLogo: "/media/rockin/Ron%20Burgundy%20Logo.png",
  wwpBanner: "/media/rockin/WWP%20banner%202.jpg",
  wwpLogo: "/media/rockin/WWP%20Black.png",
};

// Road Home LIVE concluded on Sept. 26, 2026. The public page permanently renders the
// concluded state with these upcoming events in place of the RSVP experience.
const ROAD_HOME_LIVE_CONCLUDED = true;

const upcomingEvents = [
  {
    title: "Hendo's Salute & Social",
    details: ["October 22, 2026", "Hendo's Barrel House, Temecula, California"],
    ctaLabel: "Join Us at Hendo's",
    href: "/events/salute-social-hendos-2026",
  },
  {
    title: "Clinical Approaches to Addressing Medical Trauma",
    details: ["Sacha McBain, PhD", "October 14, 2026"],
    ctaLabel: "Register for the Webinar",
    href: "/events/clinical-approaches-medical-trauma-2026",
  },
];

const militaryAffiliationOptions = [
  "Veteran",
  "Active Duty",
  "Guard/Reserve",
  "Family Member/Caregiver",
  "Provider/Community Partner",
  "Other",
];

const SERVICE_STATUSES = new Set(["Veteran", "Active Duty", "Guard/Reserve"]);

const BRANCHES = [
  "Army",
  "Army National Guard",
  "Navy",
  "Marine Corps",
  "Air Force",
  "Coast Guard",
  "Space Force",
];

const ERAS = ["Pre-9/11", "Post-9/11"];
export const WWP_MEMBER_STATUSES = ["yes", "no", "not_sure", "prefer_not_to_say"];

const MAX_PARTY_SIZE = 4;
const DISPLAY_CAPACITY = 250;
const AVAILABILITY_TRACKER_THRESHOLD = 200;
const AVAILABILITY_RPC = "get_rockin_public_availability";
const EVENT_TITLE = "Road Home LIVE: Anchored in Community";
const EVENT_NAME = "Road Home LIVE";
const EVENT_TAGLINE = "Live music. Dinner. Veterans. Families. One community.";
const CANONICAL_ROUTE_PATH = "/road-home-live";
const CALENDAR_START_UTC = "2026-09-26T21:00:00.000Z";
const CALENDAR_END_UTC = "2026-09-27T02:00:00.000Z";
const CALENDAR_TIMEZONE = "America/Chicago";
const CALENDAR_LOCATION = "115 Bourbon St., Merrionette Park, IL";
const CALENDAR_DESCRIPTION = [
  "Presented with Wounded Warrior Project.",
  "",
  "Join Road Home Program for a free evening of live music, buffet dinner, community resources, veterans, families, and supporters.",
  "",
  "Doors open at 4:00 PM.",
  "Dinner and music begin at 5:00 PM.",
].join("\n");
const partySizeOptionLabels = [
  "1 - Just me",
  "2 - 2 people total",
  "3 - 3 people total",
  "4 - 4 people total",
];

function createGuestFields() {
  return Array.from({ length: MAX_PARTY_SIZE - 1 }, () => ({
    first_name: "",
    last_name: "",
  }));
}

function createInitialRsvpForm() {
  return {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    military_status: "",
    branch_of_service: [],
    era_list: [],
    party_size: "1",
    mailing_list_opt_in: true,
    wwp_member_status: "",
    wwp_info_requested: false,
    wwp_share_consent: false,
    guests: createGuestFields(),
  };
}

export function isWwpFollowUpEligible(status) {
  return status === "no" || status === "not_sure";
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function normalizePhoneDigits(value) {
  return String(value ?? "").replace(/\D/g, "").slice(0, 10);
}

function formatPhone(digits) {
  const value = normalizePhoneDigits(digits);
  if (value.length <= 3) return value;
  if (value.length <= 6) return `(${value.slice(0, 3)}) ${value.slice(3)}`;
  return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
}

function getUrlParam(name) {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get(name)?.trim() || "";
}

function getPagePath() {
  if (typeof window === "undefined") return CANONICAL_ROUTE_PATH;
  return window.location.pathname || CANONICAL_ROUTE_PATH;
}

function getPublicEventUrl() {
  if (typeof window === "undefined") return CANONICAL_ROUTE_PATH;
  return `${window.location.origin}${CANONICAL_ROUTE_PATH}`;
}

function getReferrer() {
  if (typeof document === "undefined") return "";
  return document.referrer || "";
}

export function buildRockinRsvpPayload({
  form,
  isServiceStatus,
  partySize,
  turnstileToken = "",
  visibleGuests,
}) {
  const wwpFollowUpEligible = isWwpFollowUpEligible(form.wwp_member_status);

  return {
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    email: form.email.trim(),
    phone: form.phone.length === 10 ? form.phone : "",
    military_affiliation: form.military_status,
    branch_of_service: isServiceStatus ? form.branch_of_service : [],
    era_list: isServiceStatus ? form.era_list : [],
    party_size: partySize,
    guests: visibleGuests.map((guest) => ({
      first_name: guest.first_name.trim(),
      last_name: guest.last_name.trim(),
    })),
    dietary_notes: "",
    accessibility_notes: "",
    mailing_list_opt_in: form.mailing_list_opt_in,
    wwp_member_status: form.wwp_member_status,
    wwp_info_requested: wwpFollowUpEligible && form.wwp_info_requested === true,
    wwp_share_consent: wwpFollowUpEligible && form.wwp_share_consent === true,
    page_path: getPagePath(),
    referrer: getReferrer(),
    utm_source: getUrlParam("utm_source"),
    utm_medium: getUrlParam("utm_medium"),
    utm_campaign: getUrlParam("utm_campaign"),
    utm_term: getUrlParam("utm_term"),
    utm_content: getUrlParam("utm_content"),
    turnstile_token: turnstileToken,
  };
}

function extractResponseFromError(errorObj) {
  if (!errorObj?.context || typeof errorObj.context.json !== "function") {
    return Promise.resolve(null);
  }
  return errorObj.context.json().catch(() => null);
}

// Shown for unexpected technical failures (Edge Function errors, network
// failures, malformed responses, etc.) where the attendee can't reasonably
// fix the underlying problem themselves. Never substitute raw backend/error
// text here — see getFriendlySubmitError below.
const GENERIC_SUBMIT_ERROR_MESSAGE =
  "We weren't able to complete your registration. Please try again. If you continue to have trouble, email christopher_miller@rush.edu and we'll help you get registered.";

// Returns { message, isGeneric }. Only recognized, expected attendee-facing
// business-rule/validation responses get their specific guidance below;
// everything else (unknown codes, SERVER_ERROR, raw network/client
// exceptions) falls through to the generic failure message so we never
// surface Supabase/Edge Function/Turnstile error text to the public.
function getFriendlySubmitError(responseData, error) {
  const code = responseData?.code || "";
  const message = responseData?.error || error?.message || "";

  if (code === "EVENT_FULL" || /not enough tickets|event_full|capacity/i.test(message)) {
    return {
      message:
        "There are not enough spots left for that party size. Please reduce your party size or contact events@roadhomeprogram.org.",
      isGeneric: false,
    };
  }
  if (
    /rsvps_unique_phone_per_event_nonnull|event_id,\s*phone|phone already exists|duplicate key value violates unique constraint/i.test(
      message
    )
  ) {
    return {
      message:
        "That phone number is already attached to an RSVP for this event. Try a different phone number, leave phone blank, or contact events@roadhomeprogram.org.",
      isGeneric: false,
    };
  }
  if (code === "DUPLICATE_ATTENDEE" || /duplicate attendee/i.test(message)) {
    return {
      message:
        "One or more attendee names are already registered for this event. Please review the names and try again.",
      isGeneric: false,
    };
  }
  if (code === "VALIDATION_ERROR") {
    return {
      message: message || "Please review the highlighted fields and try again.",
      isGeneric: false,
    };
  }

  return { message: GENERIC_SUBMIT_ERROR_MESSAGE, isGeneric: true };
}

function getAttendeeName(ticket) {
  return [ticket?.attendee_first_name, ticket?.attendee_last_name].filter(Boolean).join(" ");
}

function getMobileTicketUrl(ticket) {
  return ticket?.mobile_ticket_url || ticket?.checkin_url || "";
}

const SUBMIT_MESSAGE_SUPPORT_EMAILS = ["events@roadhomeprogram.org", "christopher_miller@rush.edu"];

function renderSubmitMessage(message) {
  const email = SUBMIT_MESSAGE_SUPPORT_EMAILS.find((candidate) => message.includes(candidate));
  if (!email) return message;
  const [before, after] = message.split(email);
  return (
    <>
      {before}
      <a href={`mailto:${email}`}>{email}</a>
      {after}
    </>
  );
}

function MultiChipGroup({ errorId, idPrefix, label, onToggle, options, values }) {
  return (
    <div className="rrh-chip-field" aria-describedby={errorId}>
      <div className="rrh-chip-field__label">{label}</div>
      <div className="rrh-chip-grid" role="group" aria-label={label}>
        {options.map((option) => {
          const selected = values.includes(option);
          return (
            <button
              type="button"
              className="rrh-chip"
              aria-pressed={selected}
              id={`${idPrefix}-${option.replace(/\W+/g, "-").toLowerCase()}`}
              key={option}
              onClick={() => onToggle(option)}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function RockinRoadHomePage() {
  const [rsvpForm, setRsvpForm] = useState(() => createInitialRsvpForm());
  const [rsvpErrors, setRsvpErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitIsGenericError, setSubmitIsGenericError] = useState(false);
  const [rsvpConfirmation, setRsvpConfirmation] = useState(null);
  const [turnstileScriptReady, setTurnstileScriptReady] = useState(false);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);
  const [availability, setAvailability] = useState({
    loading: true,
    showTracker: false,
    reservedCount: null,
    displayCapacity: DISPLAY_CAPACITY,
  });
  const militaryStatusRef = useRef(null);
  const turnstileResolverRef = useRef(null);
  const turnstileTimeoutRef = useRef(null);

  useEffect(() => {
    document.title = EVENT_TITLE;
  }, []);

  useEffect(() => {
    if (ROAD_HOME_LIVE_CONCLUDED) return;
    if (!SITE_KEY) return;
    if (window.turnstile) {
      setTurnstileScriptReady(true);
      return;
    }

    const existing = document.getElementById("cf-turnstile-script");
    if (existing) {
      let tries = 0;
      const interval = window.setInterval(() => {
        tries += 1;
        if (window.turnstile) {
          setTurnstileScriptReady(true);
          window.clearInterval(interval);
        } else if (tries >= 20) {
          window.clearInterval(interval);
        }
      }, 100);
      return () => window.clearInterval(interval);
    }

    const script = document.createElement("script");
    script.id = "cf-turnstile-script";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => setTurnstileScriptReady(true);
    document.head.appendChild(script);

    return () => {
      script.onload = null;
    };
  }, []);

  useEffect(() => {
    if (
      !turnstileScriptReady ||
      !window.turnstile ||
      turnstileWidgetId ||
      !SITE_KEY ||
      !document.getElementById("rockin-turnstile-container")
    ) {
      return;
    }

    const settleTokenRequest = (token) => {
      if (!turnstileResolverRef.current) return;
      if (turnstileTimeoutRef.current) {
        window.clearTimeout(turnstileTimeoutRef.current);
        turnstileTimeoutRef.current = null;
      }
      const resolve = turnstileResolverRef.current;
      turnstileResolverRef.current = null;
      resolve(token || "");
    };

    const id = window.turnstile.render("#rockin-turnstile-container", {
      sitekey: SITE_KEY,
      execution: "execute",
      callback: (token) => settleTokenRequest(token),
      "expired-callback": () => settleTokenRequest(""),
      "error-callback": () => settleTokenRequest(""),
      retry: "auto",
    });
    setTurnstileWidgetId(id);
  }, [turnstileScriptReady, turnstileWidgetId]);

  useEffect(
    () => () => {
      if (turnstileTimeoutRef.current) {
        window.clearTimeout(turnstileTimeoutRef.current);
      }
      turnstileResolverRef.current = null;
    },
    []
  );

  async function getFreshTurnstileToken() {
    if (!window.turnstile || !turnstileWidgetId) return "";

    try {
      window.turnstile.reset(turnstileWidgetId);
    } catch {
      return "";
    }

    return new Promise((resolve) => {
      turnstileResolverRef.current = resolve;
      turnstileTimeoutRef.current = window.setTimeout(() => {
        if (turnstileResolverRef.current !== resolve) return;
        turnstileResolverRef.current = null;
        turnstileTimeoutRef.current = null;
        resolve("");
      }, 5000);
      try {
        window.turnstile.execute(turnstileWidgetId);
      } catch {
        window.clearTimeout(turnstileTimeoutRef.current);
        turnstileTimeoutRef.current = null;
        turnstileResolverRef.current = null;
        resolve("");
      }
    });
  }

  useEffect(() => {
    if (ROAD_HOME_LIVE_CONCLUDED) return;
    let cancelled = false;

    async function loadAvailability() {
      try {
        const { data, error } = await supabase.rpc(AVAILABILITY_RPC);
        if (cancelled) return;
        if (error || !data) {
          setAvailability((current) => ({ ...current, loading: false, showTracker: false }));
          return;
        }

        const row = Array.isArray(data) ? data[0] : data;
        const reservedCount = Number(row?.reserved_count);
        const displayCapacity = Number(row?.display_capacity) || DISPLAY_CAPACITY;
        const showTracker =
          row?.show_tracker === true &&
          Number.isFinite(reservedCount) &&
          reservedCount >= AVAILABILITY_TRACKER_THRESHOLD;

        setAvailability({
          loading: false,
          showTracker,
          reservedCount: showTracker ? reservedCount : null,
          displayCapacity,
        });
      } catch {
        if (!cancelled) {
          setAvailability((current) => ({ ...current, loading: false, showTracker: false }));
        }
      }
    }

    loadAvailability();

    return () => {
      cancelled = true;
    };
  }, []);

  const partySize = Math.min(
    MAX_PARTY_SIZE,
    Math.max(1, Number.parseInt(rsvpForm.party_size, 10) || 1)
  );
  const visibleGuests = useMemo(
    () => rsvpForm.guests.slice(0, Math.max(0, partySize - 1)),
    [partySize, rsvpForm.guests]
  );
  const primaryName = [rsvpForm.first_name, rsvpForm.last_name].filter(Boolean).join(" ");
  const isServiceStatus = SERVICE_STATUSES.has(rsvpForm.military_status);
  const showWwpFollowUpOptions = isWwpFollowUpEligible(rsvpForm.wwp_member_status);
  const isSubmitting = submitStatus === "submitting";

  function validateRsvpForm() {
    const nextErrors = {};
    const parsedPartySize = Number.parseInt(rsvpForm.party_size, 10);
    const requestedGuestCount =
      Number.isInteger(parsedPartySize) && parsedPartySize > 0 ? parsedPartySize - 1 : 0;
    const requestedGuests = rsvpForm.guests.slice(0, Math.max(0, requestedGuestCount));

    if (!rsvpForm.first_name.trim()) nextErrors.first_name = "Enter the primary registrant's first name.";
    if (!rsvpForm.last_name.trim()) nextErrors.last_name = "Enter the primary registrant's last name.";
    if (!isValidEmail(rsvpForm.email)) nextErrors.email = "Enter a valid email address.";
    if (rsvpForm.phone && rsvpForm.phone.length !== 10) {
      nextErrors.phone = "Enter a 10-digit phone number or leave this blank.";
    }
    if (!parsedPartySize || parsedPartySize < 1 || parsedPartySize > MAX_PARTY_SIZE) {
      nextErrors.party_size = "Choose a party size from 1 to 4.";
    }
    if (!rsvpForm.military_status) {
      nextErrors.military_status = "Choose the option that best describes your connection.";
    }
    if (!WWP_MEMBER_STATUSES.includes(rsvpForm.wwp_member_status)) {
      nextErrors.wwp_member_status = "Choose your Wounded Warrior Project registration status.";
    }
    if (isServiceStatus && rsvpForm.branch_of_service.length === 0) {
      nextErrors.branch_of_service = "Select at least one branch of service.";
    }
    if (isServiceStatus && rsvpForm.era_list.length === 0) {
      nextErrors.era_list = "Select at least one service era.";
    }

    requestedGuests.forEach((guest, index) => {
      if (!guest.first_name.trim()) {
        nextErrors[`guest_${index}_first_name`] =
          `Enter attendee ${index + 2}'s first name.`;
      }
      if (!guest.last_name.trim()) {
        nextErrors[`guest_${index}_last_name`] =
          `Enter attendee ${index + 2}'s last name.`;
      }
    });

    const names = [
      {
        index: -1,
        first_name: rsvpForm.first_name.trim(),
        last_name: rsvpForm.last_name.trim(),
      },
      ...requestedGuests.map((guest, index) => ({
        index,
        first_name: guest.first_name.trim(),
        last_name: guest.last_name.trim(),
      })),
    ].filter((attendee) => attendee.first_name && attendee.last_name);
    const seenNames = new Set();
    names.forEach((attendee) => {
      const key = `${attendee.first_name.toLowerCase()}|${attendee.last_name.toLowerCase()}`;
      if (seenNames.has(key)) {
        nextErrors.guests = "Each attendee name must be unique.";
        if (attendee.index >= 0) {
          nextErrors[`guest_${attendee.index}_last_name`] =
            "This attendee name matches another attendee.";
        }
      }
      seenNames.add(key);
    });

    return nextErrors;
  }

  function clearError(name) {
    setRsvpErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  }

  function handleFieldChange(event) {
    const { checked, name, type, value } = event.target;
    setSubmitStatus("idle");
    setSubmitMessage("");
    setRsvpConfirmation(null);
    setRsvpForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    clearError(name);
  }

  function handlePhoneChange(event) {
    const nextDigits = normalizePhoneDigits(event.target.value);
    setSubmitStatus("idle");
    setSubmitMessage("");
    setRsvpConfirmation(null);
    setRsvpForm((current) => ({
      ...current,
      phone: nextDigits,
    }));
    clearError("phone");

    if (nextDigits.length === 10 && rsvpForm.phone.length < 10) {
      window.requestAnimationFrame(() => {
        militaryStatusRef.current?.focus();
      });
    }
  }

  function handleMilitaryStatusChange(event) {
    const nextStatus = event.target.value;
    const isNextServiceStatus = SERVICE_STATUSES.has(nextStatus);

    setSubmitStatus("idle");
    setSubmitMessage("");
    setRsvpConfirmation(null);
    setRsvpForm((current) => ({
      ...current,
      military_status: nextStatus,
      branch_of_service: isNextServiceStatus ? current.branch_of_service : [],
      era_list: isNextServiceStatus ? current.era_list : [],
    }));
    clearError("military_status");
    clearError("branch_of_service");
    clearError("era_list");
  }

  function handleWwpMemberStatusChange(event) {
    const wwpMemberStatus = event.target.value;
    const eligibleForFollowUp = isWwpFollowUpEligible(wwpMemberStatus);

    setSubmitStatus("idle");
    setSubmitMessage("");
    setRsvpConfirmation(null);
    setRsvpForm((current) => ({
      ...current,
      wwp_member_status: wwpMemberStatus,
      wwp_info_requested: eligibleForFollowUp ? current.wwp_info_requested : false,
      wwp_share_consent: eligibleForFollowUp ? current.wwp_share_consent : false,
    }));
    clearError("wwp_member_status");
  }

  function toggleArrayField(field, option) {
    setSubmitStatus("idle");
    setSubmitMessage("");
    setRsvpConfirmation(null);
    setRsvpForm((current) => {
      const currentValues = current[field];
      const nextValues = currentValues.includes(option)
        ? currentValues.filter((value) => value !== option)
        : [...currentValues, option];
      return { ...current, [field]: nextValues };
    });
    clearError(field);
  }

  function handleGuestChange(index, field, value) {
    setSubmitStatus("idle");
    setSubmitMessage("");
    setRsvpConfirmation(null);
    setRsvpForm((current) => {
      const nextGuests = current.guests.map((guest, guestIndex) =>
        guestIndex === index ? { ...guest, [field]: value } : guest
      );
      return { ...current, guests: nextGuests };
    });
    clearError(`guest_${index}_${field}`);
    clearError("guests");
  }

  async function handleRsvpSubmit(event) {
    event.preventDefault();
    const nextErrors = validateRsvpForm();
    setRsvpErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setSubmitStatus("error");
      setSubmitIsGenericError(false);
      setSubmitMessage("Please review the highlighted fields and try again.");
      return;
    }

    setSubmitStatus("submitting");
    setSubmitMessage("");
    setSubmitIsGenericError(false);
    setRsvpConfirmation(null);

    const turnstileToken = await getFreshTurnstileToken();

    const payload = buildRockinRsvpPayload({
      form: rsvpForm,
      isServiceStatus,
      partySize,
      turnstileToken,
      visibleGuests,
    });

    try {
      const { data, error } = await supabase.functions.invoke("reserve-rockin-rsvp", {
        body: payload,
      });
      let responseData = data && typeof data === "object" ? data : null;
      if (!responseData && error) {
        responseData = await extractResponseFromError(error);
      }

      if (error || responseData?.ok === false) {
        const friendly = getFriendlySubmitError(responseData, error);
        setSubmitStatus("error");
        setSubmitIsGenericError(friendly.isGeneric);
        setSubmitMessage(friendly.message);
        return;
      }

      if (responseData?.ok === true) {
        setSubmitStatus("success");
        setSubmitMessage("");
        setRsvpConfirmation(responseData);
        return;
      }

      // Unexpected/malformed response shape (e.g. not JSON, missing `ok`).
      setSubmitStatus("error");
      setSubmitIsGenericError(true);
      setSubmitMessage(GENERIC_SUBMIT_ERROR_MESSAGE);
    } catch {
      // Network failure or thrown exception during submission.
      setSubmitStatus("error");
      setSubmitIsGenericError(true);
      setSubmitMessage(GENERIC_SUBMIT_ERROR_MESSAGE);
    }
  }

  return (
    <>
      <main className="rrh-page">
      <section className="rrh-hero" aria-labelledby="rrh-title">
        <div className="rrh-hero__content">
          <p className="rrh-kicker">Presented with Wounded Warrior Project</p>
          <h1 id="rrh-title">{EVENT_TITLE}</h1>
          <p className="rrh-hero__subtitle">
            {EVENT_TAGLINE}
          </p>

          <dl className="rrh-hero__meta" aria-label="Event summary">
            <div>
              <dt>When</dt>
              <dd>Saturday, September 26, 2026</dd>
            </div>
            <div>
              <dt>Time</dt>
              <dd>Doors 4:00 PM / Dinner & Music 5:00 PM</dd>
            </div>
            <div>
              <dt>Where</dt>
              <dd>
                <a
                  className="rrh-text-link"
                  href={externalLinks.venue}
                  target="_blank"
                  rel="noreferrer"
                >
                  115 Bourbon St.
                </a>
                , Merrionette Park, IL
              </dd>
            </div>
          </dl>

          <div className="rrh-hero__actions" aria-label="Primary actions">
            <a className="rrh-button rrh-button--primary" href="#rsvp">
              See Upcoming Events
            </a>
            <a
              className="rrh-button rrh-button--secondary"
              href={externalLinks.roadHome}
              target="_blank"
              rel="noreferrer"
            >
              Learn About Road Home
            </a>
          </div>
        </div>

        <div className="rrh-hero__media rrh-hero__video-card">
          <video
            className="rrh-hero__video"
            src="/media/rockin/rockin-hero-jumbotron-updated.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label="Road Home LIVE hero video"
          />
        </div>
      </section>

      <section className="rrh-details" aria-label="Event details">
        {details.map((item) => (
          <div className="rrh-details__item" key={item.label}>
            <span>{item.label}</span>
            <strong>
              {item.href ? (
                <a className="rrh-text-link" href={item.href} target="_blank" rel="noreferrer">
                  {item.value}
                </a>
              ) : (
                item.value
              )}
            </strong>
          </div>
        ))}
      </section>

      <section
        className="rrh-footer-cta rrh-footer-cta--mobile"
        aria-labelledby="rrh-mobile-cta-heading"
      >
        <p className="rrh-section__eyebrow">Saturday, September 26, 2026</p>
        <h2 id="rrh-mobile-cta-heading">
          Thank you for joining Road Home for an evening of music and connection.
        </h2>
        <div className="rrh-footer-cta__actions">
          <a className="rrh-button rrh-button--primary" href="#rsvp">
            See Upcoming Events
          </a>
          <a
            className="rrh-button rrh-button--secondary"
            href={externalLinks.roadHome}
            target="_blank"
            rel="noreferrer"
          >
            Learn About Road Home
          </a>
        </div>
      </section>

      <section className="rrh-section rrh-section--split" aria-labelledby="rrh-about-heading">
        <div>
          <p className="rrh-section__eyebrow">About the event</p>
          <h2 id="rrh-about-heading">A night built around music, dinner, and community.</h2>
        </div>
        <div className="rrh-section__body">
          <p>
            Road Home Live brings veterans, service members, families, and supporters together for
            an evening of live music, connection, and community in support of the Road Home Program.
          </p>
          <p>
            Presented with Wounded Warrior Project, this free community celebration creates space
            for veterans, families, supporters, and local resources to gather for dinner and music.
          </p>
        </div>
      </section>

      <section className="rrh-feature-grid" aria-label="Featured event partners">
        <article className="rrh-feature">
          <a
            className="rrh-feature__media"
            href={externalLinks.band}
            target="_blank"
            rel="noreferrer"
            aria-label="Learn more about The Ron Burgundy's"
          >
            <img
              className="rrh-feature__image"
              src={featureImages.bandPromo}
              alt="The Ron Burgundy's performing on stage"
            />
          </a>
          <div className="rrh-feature__content">
            <p className="rrh-section__eyebrow">Band spotlight</p>
            <a
              className="rrh-feature__logo-link rrh-feature__logo-link--band"
              href={externalLinks.band}
              target="_blank"
              rel="noreferrer"
              aria-label="Visit The Ron Burgundy's website"
            >
              <img
                className="rrh-feature__logo rrh-feature__logo--band"
                src={featureImages.bandLogo}
                alt="The Ron Burgundy's logo"
              />
            </a>
            <h2>
              <a
                className="rrh-heading-link"
                href={externalLinks.band}
                target="_blank"
                rel="noreferrer"
              >
                The Ron Burgundy&apos;s
              </a>
            </h2>
            <p>
              The Ron Burgundy&apos;s bring the yacht rock era back to life with smooth vocals,
              soaring sax, velvet harmonies, and all-live precision.
            </p>
            <p>
              Their setlist cruises through Hall &amp; Oates, Steely Dan, Toto, The Doobie Brothers,
              Boz Scaggs, and more, turning the evening into a feel-good celebration built for
              dinner, music, and community.
            </p>
          </div>
        </article>

        <article className="rrh-feature">
          <div className="rrh-feature__media">
            <img
              className="rrh-feature__image"
              src={featureImages.wwpBanner}
              alt="Wounded Warrior Project community event"
            />
          </div>
          <div className="rrh-feature__content">
            <p className="rrh-section__eyebrow">Presented with</p>
            <img
              className="rrh-feature__logo rrh-feature__logo--wwp"
              src={featureImages.wwpLogo}
              alt="Wounded Warrior Project"
            />
            <h2>Wounded Warrior Project</h2>
            <p>WWP&apos;s mission is to honor and empower wounded warriors.</p>
            <p>
              Together, Road Home Program and Wounded Warrior Project are creating an evening where
              veterans, service members, families, and supporters can connect with care, community,
              and practical resources.
            </p>
          </div>
        </article>
      </section>

      <section
        id="road-home"
        className="rrh-section rrh-section--mission"
        aria-labelledby="rrh-road-home-heading"
      >
        <div>
          <p className="rrh-section__eyebrow">Road Home mission</p>
          <h2 id="rrh-road-home-heading">A chance to connect with Road Home and local resources.</h2>
        </div>
        <div className="rrh-section__body">
          <p>
            <a
              className="rrh-text-link"
              href={externalLinks.roadHome}
              target="_blank"
              rel="noreferrer"
            >
              Road Home Program
            </a>{" "}
            supports veterans, service members, and their families with care, connection, and
            practical pathways to support. During the event, attendees can learn about Road Home and
            find approachable next steps for themselves or someone they love.
          </p>
          <p>
            Road Home Program is a member of Wounded Warrior Project&apos;s Warrior Care Network, a
            network of academic medical centers providing accelerated brain-health care for veterans
            facing PTSD and other mental health concerns. Community-based partner organizations will
            also be represented at the event to share resources and help attendees connect with
            support.
          </p>
        </div>
      </section>

      {ROAD_HOME_LIVE_CONCLUDED ? (
        <RoadHomeLiveConcludedSection />
      ) : (
      // LEGACY RSVP EXPERIENCE: the RSVP form and "After RSVP" section below are retained
      // only for historical/recovery purposes. They are not publicly rendered while
      // ROAD_HOME_LIVE_CONCLUDED is true.
      <>
      <section id="rsvp" className="rrh-rsvp" aria-labelledby="rrh-rsvp-heading">
        <div className="rrh-rsvp__content">
          <p className="rrh-section__eyebrow">Free RSVP</p>
          <h2 id="rrh-rsvp-heading">Save your spot for {EVENT_NAME}.</h2>
          <p>
            Register yourself and the people attending with you for this free community event.
            Dinner space is limited. Please reserve spots only for people in your party who plan to
            attend.
          </p>
          {availability.showTracker && (
            <div className="rrh-availability" aria-live="polite">
              <p className="rrh-availability__label">
                {availability.reservedCount > availability.displayCapacity
                  ? "Dinner capacity is very limited."
                  : "Space is filling up"}
              </p>
              <div
                className="rrh-availability__bar"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax={availability.displayCapacity}
                aria-valuenow={Math.min(
                  availability.reservedCount,
                  availability.displayCapacity
                )}
                aria-label="Dinner spot reservations"
              >
                <span
                  style={{
                    width: `${Math.min(
                      100,
                      Math.round(
                        (availability.reservedCount / availability.displayCapacity) * 100
                      )
                    )}%`,
                  }}
                />
              </div>
              {availability.reservedCount > availability.displayCapacity ? (
                <p>
                  Additional RSVPs may be reviewed based on availability. Please reserve spots only
                  for people in your party who plan to attend.
                </p>
              ) : (
                <p>
                  {availability.reservedCount} of {availability.displayCapacity} dinner spots
                  reserved. Reservations are approaching dinner capacity. Please reserve spots only
                  for people in your party who plan to attend.
                </p>
              )}
            </div>
          )}
          <p className="rrh-rsvp__verification">
            Military identification or veteran/dependent verification may be requested at check-in.
          </p>
          <p className="rrh-rsvp__note">
            Dinner will be provided as part of the event. Menu options may be limited. This event is
            intended for guests ages 12 and older. Each registration may include up to four attendees
            total.
          </p>
          <div className="rrh-rsvp-includes" aria-label="Event includes">
            <h3>Event includes</h3>
            <ul>
              <li>Free buffet dinner</li>
              <li>Live music</li>
              <li>Community resources</li>
              <li>Intended for guests ages 12 and older</li>
            </ul>
          </div>
        </div>

        <form className="rrh-rsvp__form" onSubmit={handleRsvpSubmit} noValidate>
          <fieldset className="rrh-form-section">
            <legend>Primary registrant</legend>
            <div className="rrh-form-grid">
              <label className="rrh-field" htmlFor="rrh-first-name">
                <span>First name</span>
                <input
                  id="rrh-first-name"
                  name="first_name"
                  value={rsvpForm.first_name}
                  onChange={handleFieldChange}
                  autoComplete="given-name"
                  aria-invalid={!!rsvpErrors.first_name}
                  aria-describedby={rsvpErrors.first_name ? "rrh-first-name-error" : undefined}
                />
                {rsvpErrors.first_name && (
                  <span className="rrh-field__error" id="rrh-first-name-error">
                    {rsvpErrors.first_name}
                  </span>
                )}
              </label>

              <label className="rrh-field" htmlFor="rrh-last-name">
                <span>Last name</span>
                <input
                  id="rrh-last-name"
                  name="last_name"
                  value={rsvpForm.last_name}
                  onChange={handleFieldChange}
                  autoComplete="family-name"
                  aria-invalid={!!rsvpErrors.last_name}
                  aria-describedby={rsvpErrors.last_name ? "rrh-last-name-error" : undefined}
                />
                {rsvpErrors.last_name && (
                  <span className="rrh-field__error" id="rrh-last-name-error">
                    {rsvpErrors.last_name}
                  </span>
                )}
              </label>
            </div>

            <div className="rrh-form-grid">
              <label className="rrh-field" htmlFor="rrh-email">
                <span>Email</span>
                <input
                  id="rrh-email"
                  name="email"
                  type="email"
                  value={rsvpForm.email}
                  onChange={handleFieldChange}
                  autoComplete="email"
                  aria-invalid={!!rsvpErrors.email}
                  aria-describedby={
                    rsvpErrors.email ? "rrh-email-helper rrh-email-error" : "rrh-email-helper"
                  }
                />
                <span className="rrh-field__helper" id="rrh-email-helper">
                  People in your party will use this primary email for now.
                </span>
                {rsvpErrors.email && (
                  <span className="rrh-field__error" id="rrh-email-error">
                    {rsvpErrors.email}
                  </span>
                )}
              </label>

              <label className="rrh-field" htmlFor="rrh-phone">
                <span>Phone <em>optional</em></span>
                <input
                  id="rrh-phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formatPhone(rsvpForm.phone)}
                  onChange={handlePhoneChange}
                  autoComplete="tel"
                  placeholder="(555) 123-4567"
                  maxLength="14"
                  aria-invalid={!!rsvpErrors.phone}
                  aria-describedby={rsvpErrors.phone ? "rrh-phone-error" : undefined}
                />
                {rsvpErrors.phone && (
                  <span className="rrh-field__error" id="rrh-phone-error">
                    {rsvpErrors.phone}
                  </span>
                )}
              </label>
            </div>

            <div className="rrh-form-grid">
              <label className="rrh-field" htmlFor="rrh-military-status">
                <span>Military affiliation/status</span>
                <select
                  id="rrh-military-status"
                  ref={militaryStatusRef}
                  name="military_status"
                  value={rsvpForm.military_status}
                  onChange={handleMilitaryStatusChange}
                  aria-invalid={!!rsvpErrors.military_status}
                  aria-describedby={
                    rsvpErrors.military_status ? "rrh-military-status-error" : undefined
                  }
                >
                  <option value="">Choose one</option>
                  {militaryAffiliationOptions.map((option) => (
                    <option value={option} key={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {rsvpErrors.military_status && (
                  <span className="rrh-field__error" id="rrh-military-status-error">
                    {rsvpErrors.military_status}
                  </span>
                )}
              </label>

              <label className="rrh-field" htmlFor="rrh-party-size">
                <span>Number attending, including you</span>
                <select
                  id="rrh-party-size"
                  name="party_size"
                  value={rsvpForm.party_size}
                  onChange={handleFieldChange}
                  aria-invalid={!!rsvpErrors.party_size}
                  aria-describedby={
                    rsvpErrors.party_size
                      ? "rrh-party-size-helper rrh-party-size-error"
                      : "rrh-party-size-helper"
                  }
                >
                  {Array.from({ length: MAX_PARTY_SIZE }, (_, index) => {
                    const value = String(index + 1);
                    return (
                      <option value={value} key={value}>
                        {partySizeOptionLabels[index]}
                      </option>
                    );
                  })}
                </select>
                <span className="rrh-field__helper" id="rrh-party-size-helper">
                  Dinner space is limited. Please reserve spots only for people in your party who
                  plan to attend.
                </span>
                {rsvpErrors.party_size && (
                  <span className="rrh-field__error" id="rrh-party-size-error">
                    {rsvpErrors.party_size}
                  </span>
                )}
              </label>
            </div>

            {isServiceStatus && (
              <div className="rrh-service-fields">
                <div
                  className={`rrh-service-fields__group ${
                    rsvpErrors.branch_of_service ? "rrh-service-fields__group--error" : ""
                  }`}
                >
                  <MultiChipGroup
                    label="Branch of Service"
                    options={BRANCHES}
                    values={rsvpForm.branch_of_service}
                    onToggle={(option) => toggleArrayField("branch_of_service", option)}
                    idPrefix="rrh-branch"
                    errorId={rsvpErrors.branch_of_service ? "rrh-branch-error" : undefined}
                  />
                  {rsvpErrors.branch_of_service && (
                    <span className="rrh-field__error" id="rrh-branch-error">
                      {rsvpErrors.branch_of_service}
                    </span>
                  )}
                </div>

                <div
                  className={`rrh-service-fields__group ${
                    rsvpErrors.era_list ? "rrh-service-fields__group--error" : ""
                  }`}
                >
                  <MultiChipGroup
                    label="Service Era"
                    options={ERAS}
                    values={rsvpForm.era_list}
                    onToggle={(option) => toggleArrayField("era_list", option)}
                    idPrefix="rrh-era"
                    errorId={rsvpErrors.era_list ? "rrh-era-error" : undefined}
                  />
                  {rsvpErrors.era_list && (
                    <span className="rrh-field__error" id="rrh-era-error">
                      {rsvpErrors.era_list}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="rrh-checkbox-group">
              <label className="rrh-field" htmlFor="rrh-wwp-member-status">
                <span>Are you currently registered with Wounded Warrior Project?</span>
                <select
                  id="rrh-wwp-member-status"
                  name="wwp_member_status"
                  value={rsvpForm.wwp_member_status}
                  onChange={handleWwpMemberStatusChange}
                  aria-invalid={!!rsvpErrors.wwp_member_status}
                  aria-describedby={
                    rsvpErrors.wwp_member_status ? "rrh-wwp-member-status-error" : undefined
                  }
                >
                  <option value="">Choose one</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="not_sure">Not sure</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
                {rsvpErrors.wwp_member_status && (
                  <span className="rrh-field__error" id="rrh-wwp-member-status-error">
                    {rsvpErrors.wwp_member_status}
                  </span>
                )}
              </label>

              {showWwpFollowUpOptions && (
                <>
                  <label className="rrh-check">
                    <input
                      type="checkbox"
                      name="wwp_info_requested"
                      checked={rsvpForm.wwp_info_requested}
                      onChange={handleFieldChange}
                    />
                    <span>
                      I’d like Road Home Program to email me information about Wounded Warrior
                      Project programs and Alumni opportunities.
                    </span>
                  </label>

                  <label className="rrh-check">
                    <input
                      type="checkbox"
                      name="wwp_share_consent"
                      checked={rsvpForm.wwp_share_consent}
                      onChange={handleFieldChange}
                    />
                    <span>
                      I agree that Road Home Program may share my name and contact information with
                      Wounded Warrior Project so their team may follow up with me.
                    </span>
                  </label>
                </>
              )}
            </div>
          </fieldset>

          {visibleGuests.length > 0 && (
            <fieldset className="rrh-form-section rrh-form-section--guests">
              <legend>Additional attendees</legend>
              <p className="rrh-form-section__helper">
                Additional attendee emails are not required in this version. People in your party
                will inherit the primary registrant email for now.
              </p>
              <div className="rrh-guests-list">
                {visibleGuests.map((guest, index) => (
                  <div className="rrh-guest-row" key={`guest-${index + 2}`}>
                    <h3>Attendee {index + 2}</h3>
                    <div className="rrh-form-grid">
                      <label className="rrh-field" htmlFor={`rrh-guest-${index}-first-name`}>
                        <span>Attendee first name</span>
                        <input
                          id={`rrh-guest-${index}-first-name`}
                          value={guest.first_name}
                          onChange={(event) =>
                            handleGuestChange(index, "first_name", event.target.value)
                          }
                          autoComplete="off"
                          aria-invalid={!!rsvpErrors[`guest_${index}_first_name`]}
                          aria-describedby={
                            rsvpErrors[`guest_${index}_first_name`]
                              ? `rrh-guest-${index}-first-name-error`
                              : undefined
                          }
                        />
                        {rsvpErrors[`guest_${index}_first_name`] && (
                          <span
                            className="rrh-field__error"
                            id={`rrh-guest-${index}-first-name-error`}
                          >
                            {rsvpErrors[`guest_${index}_first_name`]}
                          </span>
                        )}
                      </label>

                      <label className="rrh-field" htmlFor={`rrh-guest-${index}-last-name`}>
                        <span>Attendee last name</span>
                        <input
                          id={`rrh-guest-${index}-last-name`}
                          value={guest.last_name}
                          onChange={(event) =>
                            handleGuestChange(index, "last_name", event.target.value)
                          }
                          autoComplete="off"
                          aria-invalid={!!rsvpErrors[`guest_${index}_last_name`]}
                          aria-describedby={
                            rsvpErrors[`guest_${index}_last_name`]
                              ? `rrh-guest-${index}-last-name-error`
                              : undefined
                          }
                        />
                        {rsvpErrors[`guest_${index}_last_name`] && (
                          <span
                            className="rrh-field__error"
                            id={`rrh-guest-${index}-last-name-error`}
                          >
                            {rsvpErrors[`guest_${index}_last_name`]}
                          </span>
                        )}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              {rsvpErrors.guests && (
                <span className="rrh-field__error" id="rrh-guests-error">
                  {rsvpErrors.guests}
                </span>
              )}
            </fieldset>
          )}

          <div className="rrh-checkbox-group">
            <label className="rrh-check">
              <input
                type="checkbox"
                name="mailing_list_opt_in"
                checked={rsvpForm.mailing_list_opt_in}
                onChange={handleFieldChange}
              />
              <span>Keep me posted about Road Home events and community resources.</span>
            </label>
          </div>

          <aside className="rrh-rsvp-summary" aria-label="Registration summary">
            <h3>Registration summary</h3>
            <dl>
              <div>
                <dt>Total tickets requested</dt>
                <dd>{partySize}</dd>
              </div>
              <div>
                <dt>Primary registrant</dt>
                <dd>{primaryName || "Not entered yet"}</dd>
              </div>
              <div>
                <dt>Additional attendees</dt>
                <dd>{Math.max(0, partySize - 1)}</dd>
              </div>
              {isServiceStatus && (
                <div>
                  <dt>Service details</dt>
                  <dd>
                    {rsvpForm.branch_of_service.length || 0} branch
                    {rsvpForm.branch_of_service.length === 1 ? "" : "es"} /{" "}
                    {rsvpForm.era_list.length || 0} era
                    {rsvpForm.era_list.length === 1 ? "" : "s"}
                  </dd>
                </div>
              )}
            </dl>
            <p>
              Each attendee receives an individual ticket code after this RSVP is completed.
            </p>
          </aside>

          {submitStatus === "error" && submitMessage && (
            <div className="rrh-rsvp-feedback rrh-rsvp-feedback--error" role="alert">
              <h3>{submitIsGenericError ? "Something went wrong" : "We could not complete this RSVP."}</h3>
              <p>{renderSubmitMessage(submitMessage)}</p>
            </div>
          )}

          <div
            id="rockin-turnstile-container"
            style={{ height: 0, overflow: "hidden" }}
          />

          {rsvpConfirmation && (
            <div className="rrh-rsvp-confirmation" role="status">
              <p className="rrh-section__eyebrow">RSVP received</p>
              <h3>Your RSVP has been received.</h3>
              <p>
                A confirmation email with ticket details has been sent. Please save this
                confirmation for your records.
              </p>
              <dl>
                <div>
                  <dt>RSVP ID</dt>
                  <dd>{rsvpConfirmation.rsvp_id || "Pending"}</dd>
                </div>
                <div>
                  <dt>Party size</dt>
                  <dd>{rsvpConfirmation.party_size || partySize}</dd>
                </div>
              </dl>
              {Array.isArray(rsvpConfirmation.tickets) && rsvpConfirmation.tickets.length > 0 && (
                <div className="rrh-ticket-list">
                  <h4>Mobile tickets</h4>
                  <ul>
                    {rsvpConfirmation.tickets.map((ticket, index) => {
                      const attendeeName =
                        ticket.attendee_name || getAttendeeName(ticket) || `Attendee ${index + 1}`;
                      const mobileTicketUrl = getMobileTicketUrl(ticket);

                      return (
                        <li key={ticket.ticket_id || ticket.ticket_code || index}>
                          <div className="rrh-ticket-list__main">
                            <span>{attendeeName}</span>
                            <strong>{ticket.ticket_code || "Code pending"}</strong>
                          </div>
                          <div className="rrh-ticket-list__actions">
                            {mobileTicketUrl && (
                              <a className="rrh-ticket-list__mobile" href={mobileTicketUrl}>
                                View Mobile Ticket
                              </a>
                            )}
                            {ticket.cancel_url && (
                              <a className="rrh-ticket-list__cancel" href={ticket.cancel_url}>
                                Cancel ticket
                              </a>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {rsvpConfirmation.manage_rsvp_url && (
                <p>
                  <a href={rsvpConfirmation.manage_rsvp_url}>Manage this RSVP</a>
                </p>
              )}
              <AddToCalendar
                title={EVENT_TITLE}
                startUtc={CALENDAR_START_UTC}
                endUtc={CALENDAR_END_UTC}
                timezone={CALENDAR_TIMEZONE}
                location={CALENDAR_LOCATION}
                description={CALENDAR_DESCRIPTION}
                publicEventUrl={getPublicEventUrl()}
              />
              <p className="rrh-rsvp-confirmation__reminder">
                Military identification or veteran/dependent verification may be requested at
                check-in.
              </p>
            </div>
          )}

          {!rsvpConfirmation && (
            <button
              type="submit"
              className="rrh-button rrh-button--primary rrh-rsvp__submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Reserving your spot..." : "Complete Free RSVP"}
            </button>
          )}
        </form>
      </section>

      <section className="rrh-section rrh-section--next" aria-labelledby="rrh-next-heading">
        <div>
          <p className="rrh-section__eyebrow">After RSVP</p>
          <h2 id="rrh-next-heading">What attendees can expect next.</h2>
        </div>
        <div className="rrh-section__body">
          <p>
            After completing the RSVP, please save the in-page confirmation and ticket codes. A
            confirmation email with ticket details will also be sent for your records.
          </p>
        </div>
      </section>
      </>
      )}

      <section
        className="rrh-footer-cta rrh-footer-cta--desktop"
        aria-labelledby="rrh-footer-heading"
      >
        <p className="rrh-section__eyebrow">Saturday, September 26, 2026</p>
        <h2 id="rrh-footer-heading">
          Thank you for joining Road Home for an evening of music and connection.
        </h2>
        <div className="rrh-footer-cta__actions">
          <a className="rrh-button rrh-button--primary" href="#rsvp">
            See Upcoming Events
          </a>
          <a
            className="rrh-button rrh-button--secondary"
            href={externalLinks.roadHome}
            target="_blank"
            rel="noreferrer"
          >
            Learn About Road Home
          </a>
        </div>
      </section>
      </main>
      <RockinSiteFooter />
    </>
  );
}

// Keeps id="rsvp" so existing #rsvp anchors and the page CTAs land on this section.
function RoadHomeLiveConcludedSection() {
  return (
    <section id="rsvp" className="rrh-rsvp" aria-labelledby="rrh-concluded-heading">
      <div className="rrh-rsvp__content">
        <p className="rrh-section__eyebrow">Event concluded</p>
        <h2 id="rrh-concluded-heading">{EVENT_NAME} has concluded</h2>
        <p>
          Thank you to everyone who joined us for {EVENT_TITLE}. We would love to see you at an
          upcoming Road Home Program event.
        </p>
      </div>

      <div className="rrh-rsvp__card">
        <h3>Upcoming Road Home events</h3>
        <ul className="rrh-upcoming-events">
          {upcomingEvents.map((event) => (
            <li className="rrh-upcoming-event" key={event.href}>
              <h4>{event.title}</h4>
              {event.details.map((detail) => (
                <p key={detail}>{detail}</p>
              ))}
              <a className="rrh-button rrh-button--primary" href={event.href}>
                {event.ctaLabel}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function RockinSiteFooter() {
  return (
    <footer className="rrh-site-footer tdp-footer" role="contentinfo">
      <div className="tdp-footer__inner">
        <div className="tdp-footer__brand">
          <img
            src={ShieldFooterIMG}
            alt=""
            aria-hidden="true"
            className="tdp-footer__shield"
            decoding="async"
          />
          <div className="tdp-footer__brandtext">
            <div className="tdp-footer__title">Road Home Program</div>
            <div className="tdp-footer__org">
              The National Center of Excellence for Veterans and Their Families at Rush
            </div>
            <div className="tdp-footer__contact">
              1645 W. Jackson Blvd., Suite 602, Chicago, IL 60612
              <span className="tdp-dot" aria-hidden>
                •
              </span>
              <a href="tel:13129428387">(312) 942-8387 (VETS)</a>
            </div>
          </div>
        </div>

        <nav className="tdp-footer__nav" aria-label="Footer">
          <a href="https://roadhomeprogram.org/family-center/">Help for Families</a>
          <a href="https://roadhomeprogram.org/get-care/">Get Care</a>
          <a href="https://roadhomeprogram.org/accelerated-treatment-program/">
            Accelerated Treatment Program
          </a>
          <a href="https://roadhomeprogram.org/outpatient-program/">Outpatient Program</a>
          <a href="https://roadhomeprogram.org/outreach-and-events/">Outreach and Events</a>
          <a href="https://roadhomeprogram.org/contact-us/">General Information</a>
          <a
            href="https://www.rush.edu/website-privacy-statement"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Statement
          </a>
          <a href="https://www.rush.edu/disclaimer" target="_blank" rel="noreferrer">
            Disclaimer
          </a>
          <a
            href="https://www.rush.edu/sites/default/files/rush-nondiscrimination-policy.pdf"
            target="_blank"
            rel="noreferrer"
          >
            Nondiscrimination Policy
          </a>
        </nav>

        <div className="tdp-footer__legal">
          © {new Date().getFullYear()} Road Home Program at Rush.
        </div>
      </div>
    </footer>
  );
}
